import React, { useState, useEffect, useCallback } from "react";
import { Link, NavLink, useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../redux/actions/authAction";
import { dialog } from "../Common/TreasuryDialog";
import logoFull from "../../images/logo_white_transparent.png";
import axios from "axios";

const API = process.env.REACT_APP_API_URL || "";

const AdminShell = ({ children, pageTitle }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const { auth } = useSelector((state) => state);
  const [collapsed, setCollapsed] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(
    location.pathname.startsWith("/admin/settings")
  );
  const [pendingCounts, setPendingCounts] = useState({ pendingFunds: 0, pendingWithdrawals: 0, pendingProperties: 0, total: 0 });
  const [showNotifDropdown, setShowNotifDropdown] = useState(false);

  const token = auth?.data?.accesstoken || auth?.data?.access_token;

  const adminName =
    auth?.token?.name ||
    auth?.data?.user?.name ||
    "Admin";

  const fetchPendingCounts = useCallback(() => {
    if (!token) return;
    axios.get(`${API}/api/getAdminPendingCounts`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { if (res.data.status === 1) setPendingCounts(res.data); })
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    fetchPendingCounts();
    const interval = setInterval(fetchPendingCounts, 60000); // refresh every 60s
    return () => clearInterval(interval);
  }, [fetchPendingCounts]);

  // Close notif dropdown on outside click
  useEffect(() => {
    if (!showNotifDropdown) return;
    const handler = () => setShowNotifDropdown(false);
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, [showNotifDropdown]);

  const handleLogout = async () => {
    const confirmed = await dialog.confirm("Logout", "Are you sure you want to logout?");
    if (confirmed) {
      dispatch(logout());
      history.push("/admin/login");
    }
  };

  const Badge = ({ count }) => {
    if (!count) return null;
    return (
      <span style={{
        background: "#ef4444",
        color: "#fff",
        borderRadius: 20,
        fontSize: 10,
        fontWeight: 700,
        padding: "1px 6px",
        minWidth: 18,
        textAlign: "center",
        lineHeight: "16px",
        display: "inline-block",
        marginLeft: "auto",
        flexShrink: 0,
      }}>
        {count > 99 ? "99+" : count}
      </span>
    );
  };

  const navItems = [
    {
      path: "/admin/dashboard",
      label: "Dashboard",
      badge: pendingCounts.total > 0 ? pendingCounts.total : 0,
      icon: (
        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
        </svg>
      ),
    },
    {
      path: "/admin/users",
      label: "Users",
      icon: (
        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      ),
    },
    {
      path: "/admin/properties",
      label: "Properties",
      badge: pendingCounts.pendingProperties,
      icon: (
        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
          <polyline points="9 22 9 12 15 12 15 22" />
        </svg>
      ),
    },
    {
      path: "/admin/tokenization",
      label: "Tokenization",
      icon: (
        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" />
          <path d="M12 6v12" />
          <path d="M6 12h12" />
        </svg>
      ),
    },
    {
      path: "/admin/blockchaindata",
      label: "Blockchain Data",
      icon: (
        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <ellipse cx="12" cy="5" rx="9" ry="3" />
          <path d="M21 12c0 1.66-4 3-9 3s-9-1.34-9-3" />
          <path d="M3 5v14c0 1.66 4 3 9 3s9-1.34 9-3V5" />
        </svg>
      ),
    },
    {
      path: "/admin/requestFund",
      label: "Fund Requests",
      badge: pendingCounts.pendingFunds,
      icon: (
        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <line x1="12" y1="1" x2="12" y2="23" />
          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        </svg>
      ),
    },
    {
      path: "/admin/finance",
      label: "Finance",
      icon: (
        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <rect x="2" y="5" width="20" height="14" rx="2" />
          <line x1="2" y1="10" x2="22" y2="10" />
        </svg>
      ),
    },
    {
      path: "/admin/withdrawals",
      label: "Withdrawals",
      badge: pendingCounts.pendingWithdrawals,
      icon: (
        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <polyline points="17 1 21 5 17 9" />
          <path d="M3 11V9a4 4 0 0 1 4-4h14" />
          <polyline points="7 23 3 19 7 15" />
          <path d="M21 13v2a4 4 0 0 1-4 4H3" />
        </svg>
      ),
    },
    {
      path: "/admin/fees",
      label: "Fees & Invoices",
      icon: (
        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
        </svg>
      ),
    },
    {
      path: "/admin/news",
      label: "News",
      icon: (
        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M4 22h16a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2H8a2 2 0 0 0-2 2v16a2 2 0 0 1-2 2Zm0 0a2 2 0 0 1-2-2v-9c0-1.1.9-2 2-2h2" />
          <path d="M18 14h-8" />
          <path d="M15 18h-5" />
          <path d="M10 6h8v4h-8V6Z" />
        </svg>
      ),
    },
    {
      path: "/admin/integrations",
      label: "Integrations",
      icon: (
        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <rect x="2" y="7" width="6" height="10" rx="1" />
          <rect x="16" y="7" width="6" height="10" rx="1" />
          <path d="M8 12h8" />
          <circle cx="12" cy="12" r="2" />
        </svg>
      ),
    },
    {
      path: "/admin/alinma",
      label: "Virtual IBAN",
      icon: (
        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <rect x="2" y="5" width="20" height="14" rx="2" />
          <path d="M2 10h20" />
          <path d="M6 15h4" />
          <path d="M14 15h4" />
        </svg>
      ),
    },
    {
      path: "/admin/contact-messages",
      label: "Contact Messages",
      icon: (
        <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      ),
    },
  ];

  // Settings sub-items
  const settingsItems = [
    {
      path: "/admin/settings/users",
      label: "Users & Roles",
      icon: (
        <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      ),
    },
    {
      path: "/admin/settings/kyc",
      label: "KYC & Compliance",
      icon: (
        <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
      ),
    },
    {
      path: "/admin/settings/notifications",
      label: "Notifications",
      icon: (
        <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
      ),
    },
    {
      path: "/admin/settings/platform",
      label: "Platform Config",
      icon: (
        <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="3" />
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
        </svg>
      ),
    },
    {
      path: "/admin/settings/audit",
      label: "Audit Log",
      icon: (
        <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <polyline points="10 9 9 9 8 9" />
        </svg>
      ),
    },
  ];

  const isSettingsActive = location.pathname.startsWith("/admin/settings");
  const sidebarW = collapsed ? 68 : 236;

  // Notification dropdown items
  const notifItems = [
    pendingCounts.pendingFunds > 0 && {
      label: `${pendingCounts.pendingFunds} pending fund request${pendingCounts.pendingFunds > 1 ? "s" : ""}`,
      sub: "Awaiting approval",
      to: "/admin/requestFund",
      color: "#d97706",
      icon: "💰",
    },
    pendingCounts.pendingWithdrawals > 0 && {
      label: `${pendingCounts.pendingWithdrawals} pending withdrawal${pendingCounts.pendingWithdrawals > 1 ? "s" : ""}`,
      sub: "Awaiting processing",
      to: "/admin/withdrawals",
      color: "#6366f1",
      icon: "🏦",
    },
    pendingCounts.pendingProperties > 0 && {
      label: `${pendingCounts.pendingProperties} property listing${pendingCounts.pendingProperties > 1 ? "s" : ""} under review`,
      sub: "Submitted by users",
      to: "/admin/properties",
      color: "#0e3725",
      icon: "🏢",
    },
  ].filter(Boolean);

  return (
    <div className="admin-shell" dir="ltr" style={{ display: "flex", minHeight: "100vh", background: "#f4f6fa", fontFamily: "'Inter','Segoe UI',sans-serif", direction: "ltr", textAlign: "left" }}>
      {/* ── Sidebar ── */}
      <aside
        style={{
          width: sidebarW,
          minWidth: sidebarW,
          background: "linear-gradient(180deg,#1a2035 0%,#0d1117 100%)",
          display: "flex",
          flexDirection: "column",
          position: "fixed",
          top: 0,
          left: 0,
          bottom: 0,
          zIndex: 200,
          boxShadow: "4px 0 24px rgba(0,0,0,0.25)",
          transition: "width 0.22s ease, min-width 0.22s ease",
          overflow: "hidden",
        }}
      >
        {/* Logo row */}
        <div
          style={{
            height: 64,
            display: "flex",
            alignItems: "center",
            padding: collapsed ? "0 16px" : "0 20px",
            gap: 12,
            borderBottom: "1px solid rgba(255,255,255,0.07)",
            flexShrink: 0,
          }}
        >
          {collapsed ? (
            /* Collapsed: show only the star icon portion (crop left 34px) */
            <img src={logoFull} alt="Treasury" style={{ width: 34, height: 34, objectFit: "cover", objectPosition: "left center", flexShrink: 0 }} />
          ) : (
            /* Expanded: show full logo — it has transparent background, perfect for dark sidebar */
            <img src={logoFull} alt="Treasury" style={{ height: 36, maxWidth: 160, objectFit: "contain", flexShrink: 0 }} />
          )}
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: "12px 0", overflowY: "auto", overflowX: "hidden" }}>
          {/* Main nav items */}
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: collapsed ? "11px 22px" : "11px 20px",
                color: "rgba(255,255,255,0.5)",
                textDecoration: "none",
                fontSize: 13.5,
                fontWeight: 500,
                borderLeft: "3px solid transparent",
                transition: "all 0.18s",
                whiteSpace: "nowrap",
              }}
              activeStyle={{
                color: "#c9a84c",
                background: "rgba(201,168,76,0.12)",
                borderLeft: "3px solid #c9a84c",
              }}
            >
              <span style={{ flexShrink: 0, opacity: 0.85, position: "relative" }}>
                {item.icon}
                {/* Dot badge when collapsed */}
                {collapsed && item.badge > 0 && (
                  <span style={{
                    position: "absolute",
                    top: -3,
                    right: -4,
                    width: 8,
                    height: 8,
                    background: "#ef4444",
                    borderRadius: "50%",
                    border: "1.5px solid #1a2035",
                  }} />
                )}
              </span>
              {!collapsed && (
                <>
                  <span style={{ flex: 1 }}>{item.label}</span>
                  {item.badge > 0 && <Badge count={item.badge} />}
                </>
              )}
            </NavLink>
          ))}

          {/* Divider */}
          <div style={{ margin: "10px 16px", borderTop: "1px solid rgba(255,255,255,0.07)" }} />

          {/* Settings accordion */}
          <button
            onClick={() => { if (!collapsed) setSettingsOpen(!settingsOpen); else history.push("/admin/settings/users"); }}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: collapsed ? "11px 22px" : "11px 20px",
              color: isSettingsActive ? "#c9a84c" : "rgba(255,255,255,0.5)",
              background: isSettingsActive ? "rgba(201,168,76,0.12)" : "transparent",
              borderLeft: isSettingsActive ? "3px solid #c9a84c" : "3px solid transparent",
              border: "none",
              width: "100%",
              textAlign: "left",
              fontSize: 13.5,
              fontWeight: 500,
              cursor: "pointer",
              transition: "all 0.18s",
              whiteSpace: "nowrap",
            }}
          >
            {/* Gear icon */}
            <span style={{ flexShrink: 0, opacity: 0.85 }}>
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="3" />
                <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
              </svg>
            </span>
            {!collapsed && (
              <>
                <span style={{ flex: 1 }}>Settings</span>
                <svg
                  width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"
                  style={{ transform: settingsOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s", marginRight: 4 }}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </>
            )}
          </button>

          {/* Settings sub-items */}
          {!collapsed && settingsOpen && (
            <div style={{ background: "rgba(0,0,0,0.2)" }}>
              {settingsItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "9px 20px 9px 44px",
                    color: "rgba(255,255,255,0.4)",
                    textDecoration: "none",
                    fontSize: 12.5,
                    fontWeight: 500,
                    borderLeft: "3px solid transparent",
                    transition: "all 0.18s",
                    whiteSpace: "nowrap",
                  }}
                  activeStyle={{
                    color: "#c9a84c",
                    background: "rgba(201,168,76,0.08)",
                    borderLeft: "3px solid #c9a84c",
                  }}
                >
                  <span style={{ flexShrink: 0, opacity: 0.8 }}>{item.icon}</span>
                  <span>{item.label}</span>
                </NavLink>
              ))}
            </div>
          )}
        </nav>

        {/* Collapse button */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          title={collapsed ? "Expand" : "Collapse"}
          style={{
            background: "none",
            border: "none",
            borderTop: "1px solid rgba(255,255,255,0.07)",
            color: "#c9a84c",
            padding: "14px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: collapsed ? "center" : "flex-end",
            flexShrink: 0,
          }}
        >
          <svg
            width="15"
            height="15"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            viewBox="0 0 24 24"
            style={{ transform: collapsed ? "rotate(180deg)" : "none", transition: "transform 0.22s" }}
          >
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </button>
      </aside>

      {/* ── Main area ── */}
      <div
        style={{
          marginLeft: sidebarW,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          transition: "margin-left 0.22s ease",
          minWidth: 0,
        }}
      >
        {/* Top bar */}
        <header
          style={{
            height: 64,
            background: "#fff",
            borderBottom: "1px solid #e8eaf0",
            padding: "0 28px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            position: "sticky",
            top: 0,
            zIndex: 100,
            boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            flexShrink: 0,
          }}
        >
          <div>
            <h1 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#111827" }}>
              {pageTitle || "Admin Panel"}
            </h1>
            <p style={{ margin: 0, fontSize: 11, color: "#9ca3af", marginTop: 1 }}>Treasury Admin Zone</p>
          </div>

          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <Link
              to="/"
              target="_blank"
              style={{
                fontSize: 12,
                color: "#6b7280",
                textDecoration: "none",
                display: "flex",
                alignItems: "center",
                gap: 5,
                padding: "5px 10px",
                borderRadius: 6,
                border: "1px solid #e5e7eb",
                transition: "all 0.18s",
              }}
            >
              <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                <polyline points="15 3 21 3 21 9" />
                <line x1="10" y1="14" x2="21" y2="3" />
              </svg>
              View Site
            </Link>

            {/* Notification Bell */}
            <div style={{ position: "relative" }} onClick={(e) => e.stopPropagation()}>
              <button
                onClick={() => setShowNotifDropdown((v) => !v)}
                title="Pending actions"
                style={{
                  position: "relative",
                  background: "none",
                  border: "1px solid #e5e7eb",
                  borderRadius: 8,
                  width: 36,
                  height: 36,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: pendingCounts.total > 0 ? "#d97706" : "#6b7280",
                  transition: "all 0.18s",
                }}
              >
                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
                {pendingCounts.total > 0 && (
                  <span style={{
                    position: "absolute",
                    top: -5,
                    right: -5,
                    background: "#ef4444",
                    color: "#fff",
                    borderRadius: 20,
                    fontSize: 9,
                    fontWeight: 700,
                    padding: "1px 5px",
                    minWidth: 16,
                    textAlign: "center",
                    lineHeight: "14px",
                    border: "1.5px solid #fff",
                  }}>
                    {pendingCounts.total > 99 ? "99+" : pendingCounts.total}
                  </span>
                )}
              </button>

              {/* Dropdown */}
              {showNotifDropdown && (
                <div style={{
                  position: "absolute",
                  top: "calc(100% + 8px)",
                  right: 0,
                  width: 300,
                  background: "#fff",
                  borderRadius: 12,
                  boxShadow: "0 8px 32px rgba(0,0,0,0.14)",
                  border: "1px solid #e5e7eb",
                  zIndex: 999,
                  overflow: "hidden",
                }}>
                  <div style={{ padding: "14px 16px", borderBottom: "1px solid #f3f4f6", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>Pending Actions</span>
                    {pendingCounts.total > 0 && (
                      <span style={{ fontSize: 11, fontWeight: 600, background: "#fef3c7", color: "#92400e", padding: "2px 8px", borderRadius: 20 }}>
                        {pendingCounts.total} pending
                      </span>
                    )}
                  </div>
                  {notifItems.length === 0 ? (
                    <div style={{ padding: "24px 16px", textAlign: "center", color: "#9ca3af", fontSize: 13 }}>
                      <div style={{ fontSize: 28, marginBottom: 6 }}>✅</div>
                      All caught up — no pending actions
                    </div>
                  ) : (
                    notifItems.map((n, idx) => (
                      <Link
                        key={idx}
                        to={n.to}
                        onClick={() => setShowNotifDropdown(false)}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 12,
                          padding: "12px 16px",
                          textDecoration: "none",
                          borderBottom: idx < notifItems.length - 1 ? "1px solid #f9fafb" : "none",
                          background: "#fff",
                          transition: "background 0.15s",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#fafafa")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
                      >
                        <div style={{
                          width: 36,
                          height: 36,
                          borderRadius: 8,
                          background: `${n.color}15`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontSize: 18,
                          flexShrink: 0,
                        }}>
                          {n.icon}
                        </div>
                        <div>
                          <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#111827" }}>{n.label}</p>
                          <p style={{ margin: "2px 0 0", fontSize: 11, color: "#9ca3af" }}>{n.sub}</p>
                        </div>
                        <svg width="14" height="14" fill="none" stroke="#9ca3af" strokeWidth="2" viewBox="0 0 24 24" style={{ marginLeft: "auto", flexShrink: 0 }}>
                          <polyline points="9 18 15 12 9 6" />
                        </svg>
                      </Link>
                    ))
                  )}
                  <div style={{ padding: "10px 16px", borderTop: "1px solid #f3f4f6" }}>
                    <Link
                      to="/admin/dashboard"
                      onClick={() => setShowNotifDropdown(false)}
                      style={{ fontSize: 12, color: "#c9a84c", fontWeight: 600, textDecoration: "none" }}
                    >
                      View Dashboard →
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <div style={{ width: 1, height: 28, background: "#e5e7eb" }} />

            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg,#c9a84c,#7a5c10)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontWeight: 800,
                  fontSize: 14,
                  flexShrink: 0,
                }}
              >
                {adminName.charAt(0).toUpperCase()}
              </div>
              <div>
                <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#111827" }}>{adminName}</p>
                <p style={{ margin: 0, fontSize: 11, color: "#9ca3af" }}>Administrator</p>
              </div>
            </div>

            <button
              onClick={handleLogout}
              style={{
                background: "none",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                padding: "6px 14px",
                fontSize: 12,
                color: "#6b7280",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
                transition: "all 0.18s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "#ef4444";
                e.currentTarget.style.color = "#ef4444";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "#e5e7eb";
                e.currentTarget.style.color = "#6b7280";
              }}
            >
              <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              Logout
            </button>
          </div>
        </header>

        {/* Page content */}
        <main style={{ flex: 1, padding: "28px 32px", overflowY: "auto" }}>
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminShell;
